import React, { useState, useEffect, useMemo } from 'react';
import {
  LineChart as RechartsLine,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { adminCriticalityApi } from '../services/api';
import Card from './common/Card';
import MultiOESRadarChart from './MultiOESRadarChart';
import PostureComplianceBarChart from './PostureComplianceBarChart';

// Line colors for posture-over-time (elegant, distinct)
const POSTURE_LINE_COLORS = ['#0d9488', '#2563eb', '#7c3aed', '#db2777', '#ea580c', '#ca8a04', '#059669', '#4f46e5'];

export default function AdminPostureDashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await adminCriticalityApi.getPostureDashboardMetrics();
        setMetrics(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load posture metrics');
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, []);

  const { 
    oes_posture_data = [], 
    posture_over_time = [],
  } = metrics || {};

  // Posture over time: one line per OES, x = submitted_at date, y = overall_compliance (0–100)
  const postureOverTimeSeries = useMemo(() => {
    if (!posture_over_time.length) return [];
    const seen = new Map();
    posture_over_time.forEach(({ oes_id, oes_name }) => {
      if (!seen.has(oes_id)) seen.set(oes_id, { key: `oes_${oes_id}`, name: (oes_name || `OES ${oes_id}`).slice(0, 24) });
    });
    return Array.from(seen.values());
  }, [posture_over_time]);

  const postureOverTimeChartData = useMemo(() => {
    if (!posture_over_time.length) return [];
    const dateToRow = {};
    posture_over_time.forEach(({ oes_id, submitted_at, overall_compliance }) => {
      const dateStr = (submitted_at || '').slice(0, 10);
      if (!dateStr) return;
      const key = `oes_${oes_id}`;
      if (!dateToRow[dateStr]) dateToRow[dateStr] = { date: dateStr };
      dateToRow[dateStr][key] = overall_compliance;
    });
    return Object.keys(dateToRow)
      .sort()
      .map((date) => dateToRow[date]);
  }, [posture_over_time]);

  const postureOverTimeKeyToName = useMemo(() => {
    const map = {};
    postureOverTimeSeries.forEach((s) => { map[s.key] = s.name; });
    return map;
  }, [postureOverTimeSeries]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-200 border-t-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600 text-sm">Loading posture dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  if (oes_posture_data.length === 0 && posture_over_time.length === 0) {
    return (
      <Card className="p-6">
        <p className="text-center text-slate-500">No posture assessment data available yet.</p>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Multi-OES Radar Chart */}
      {oes_posture_data.length > 0 && (
        <Card className="p-5">
          <h3 className="text-sm font-medium text-slate-500 tracking-wide mb-4">COMPLIANCE BY DOMAIN</h3>
          <div className="flex justify-center">
            <MultiOESRadarChart data={oes_posture_data} size={350} />
          </div>
        </Card>
      )}

      {/* Overall Compliance Bar Chart */}
      {oes_posture_data.length > 0 && (
        <Card className="p-5">
          <h3 className="text-sm font-medium text-slate-500 tracking-wide mb-4">OVERALL COMPLIANCE BY OES</h3>
          <PostureComplianceBarChart data={oes_posture_data} />
        </Card>
      )}

      {/* Posture over time – full width, same style as Criticality over time */}
      <Card className="p-5 overflow-hidden lg:col-span-2">
        <h3 className="text-sm font-medium text-slate-500 uppercase tracking-wide mb-3">Compliance over time</h3>
        {postureOverTimeChartData.length > 0 && postureOverTimeSeries.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <RechartsLine data={postureOverTimeChartData} margin={{ top: 12, right: 24, bottom: 36, left: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
              <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} unit="%" />
              <Tooltip
                formatter={(value, name) => (value != null ? [`${value}%`, postureOverTimeKeyToName[name] || name] : [null, name])}
                labelFormatter={(label) => `Date: ${label}`}
                contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 12, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.07)' }}
              />
              {postureOverTimeSeries.map((s, i) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={s.name}
                  stroke={POSTURE_LINE_COLORS[i % POSTURE_LINE_COLORS.length]}
                  strokeWidth={2.5}
                  dot={{ fill: '#fff', stroke: POSTURE_LINE_COLORS[i % POSTURE_LINE_COLORS.length], strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, strokeWidth: 2 }}
                  connectNulls
                />
              ))}
            </RechartsLine>
          </ResponsiveContainer>
        ) : (
          <div className="h-[260px] flex items-center justify-center text-slate-500 text-sm">
            Complete posture assessments over time to see compliance trends per OES
          </div>
        )}
      </Card>
    </div>
  );
}
